"use client"

import type React from "react"

import { useState, useEffect } from "react"
import { createClient } from "@/lib/supabase/client"
import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Users, Plus, Calendar, Lock } from "lucide-react"
import { format } from "date-fns"
import Link from "next/link"
import type { User } from "@supabase/supabase-js"
import { usePlatform, triggerHaptic } from "@/hooks/use-platform"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Switch } from "@/components/ui/switch"
import { Label } from "@/components/ui/label"

interface SurfParty {
  id: string
  title: string
  party_date: string
  start_time: string | null
  time_of_day: string | null
  activity_type: "surf" | "kite"
  is_public: boolean
  organizer_id: string
  organizer_name: string
  participant_count: number
  is_participant: boolean
}

interface SurfPartyListProps {
  spotId: string
  onCreateParty: () => void
  onPartyClick: (partyId: string) => void
  user?: User | null // Added user prop for auth checking
  isPlus?: boolean
}

const getDummyParties = (): SurfParty[] => {
  const today = new Date()
  return [
    {
      id: "dummy-1",
      title: "Morning Dawn Patrol",
      party_date: new Date(today.getTime() + 1 * 24 * 60 * 60 * 1000).toISOString().split("T")[0],
      start_time: "06:00",
      time_of_day: null,
      activity_type: "surf",
      is_public: true,
      organizer_id: "dummy",
      organizer_name: "Sarah M.",
      participant_count: 5,
      is_participant: false,
    },
    {
      id: "dummy-2",
      title: "Sunset Session & BBQ",
      party_date: new Date(today.getTime() + 2 * 24 * 60 * 60 * 1000).toISOString().split("T")[0],
      start_time: "17:30",
      time_of_day: null,
      activity_type: "surf",
      is_public: true,
      organizer_id: "dummy",
      organizer_name: "Mike T.",
      participant_count: 8,
      is_participant: false,
    },
    {
      id: "dummy-3",
      title: "Weekend Kite Session",
      party_date: new Date(today.getTime() + 4 * 24 * 60 * 60 * 1000).toISOString().split("T")[0],
      start_time: null,
      time_of_day: "afternoon",
      activity_type: "kite",
      is_public: false,
      organizer_id: "dummy",
      organizer_name: "Alex K.",
      participant_count: 4,
      is_participant: false,
    },
    {
      id: "dummy-4",
      title: "Big Swell Party",
      party_date: new Date(today.getTime() + 5 * 24 * 60 * 60 * 1000).toISOString().split("T")[0],
      start_time: "08:00",
      time_of_day: null,
      activity_type: "surf",
      is_public: true,
      organizer_id: "dummy",
      organizer_name: "Chris P.",
      participant_count: 12,
      is_participant: false,
    },
  ]
}

export function SurfPartyList({ spotId, onCreateParty, onPartyClick, user, isPlus }: SurfPartyListProps) {
  const [parties, setParties] = useState<SurfParty[]>([])
  const [loading, setLoading] = useState(true)
  const [currentUserId, setCurrentUserId] = useState<string | null>(null)
  const [currentUserDbId, setCurrentUserDbId] = useState<string | null>(null)
  const [joinDialogOpen, setJoinDialogOpen] = useState(false)
  const [selectedPartyId, setSelectedPartyId] = useState<string | null>(null)
  const [receiveNotifications, setReceiveNotifications] = useState(true) // Default to true

  useEffect(() => {
    if (!user) {
      setParties(getDummyParties())
      setLoading(false)
      return
    }

    fetchCurrentUser()
    fetchParties()
  }, [spotId, user])

  const fetchCurrentUser = async () => {
    const supabase = createClient()
    const {
      data: { user },
    } = await supabase.auth.getUser()
    setCurrentUserId(user?.id || null)

    if (user) {
      const { data: userData } = await supabase.from("users").select("id").eq("auth_id", user.id).single()

      setCurrentUserDbId(userData?.id || null)
    }
  }

  const fetchParties = async () => {
    const supabase = createClient()
    setLoading(true)

    try {
      const { data: partiesData, error: partiesError } = await supabase
        .from("surf_parties")
        .select(`
          id,
          title,
          party_date,
          start_time,
          time_of_day,
          activity_type,
          is_public,
          organizer_id,
          organizer:users!surf_parties_organizer_id_fkey(id, name, first_name, last_name, email)
        `)
        .eq("surf_spot_id", spotId)
        .gte("party_date", new Date().toISOString().split("T")[0])
        .order("party_date", { ascending: true })

      if (partiesError) throw partiesError

      const partyIds = partiesData?.map((p) => p.id) || []
      const { data: participantsData } = await supabase
        .from("surf_party_participants")
        .select("party_id, user_id")
        .in("party_id", partyIds)

      const {
        data: { user },
      } = await supabase.auth.getUser()
      const userId = user?.id

      const partiesWithCounts =
        partiesData?.map((party) => {
          const participants = participantsData?.filter((p) => p.party_id === party.id) || []
          
          // Get organizer display name: prefer first_name + last_name, fallback to name, then email
          const organizer = party.organizer as any
          const organizerName = 
            (organizer?.first_name && organizer?.last_name)
              ? `${organizer.first_name} ${organizer.last_name}`
              : organizer?.name || organizer?.email?.split("@")[0] || "Unknown"
          
          return {
            id: party.id,
            title: party.title,
            party_date: party.party_date,
            start_time: party.start_time,
            time_of_day: party.time_of_day,
            activity_type: party.activity_type || "surf",
            is_public: party.is_public,
            organizer_id: party.organizer_id,
            organizer_name: organizerName,
            participant_count: participants.length,
            is_participant: participants.some((p) => p.user_id === userId),
          }
        }) || []

      setParties(partiesWithCounts)
    } catch (error) {
      console.error("Error fetching parties:", error)
    } finally {
      setLoading(false)
    }
  }

  const handleJoinClick = (partyId: string, e: React.MouseEvent) => {
    e.stopPropagation()
    triggerHaptic('impact')
    setSelectedPartyId(partyId)
    setReceiveNotifications(true) // Reset to default (true) when opening dialog
    setJoinDialogOpen(true)
  }

  const handleJoinParty = async () => {
    if (!selectedPartyId) return

    triggerHaptic('selection')
    const supabase = createClient()
    const {
      data: { user },
    } = await supabase.auth.getUser()

    if (!user) {
      triggerHaptic('error')
      alert("Please sign in to join a surf party")
      setJoinDialogOpen(false)
      return
    }

    try {
      const party = parties.find((p) => p.id === selectedPartyId)

      if (party && !party.is_public && currentUserDbId) {
        const { data: invite, error: inviteError } = await supabase
          .from("surf_party_invites")
          .select("id")
          .eq("party_id", selectedPartyId)
          .eq("invitee_id", currentUserDbId)
          .single()

        if (inviteError || !invite) {
          triggerHaptic('error')
          alert("This is a private party. You need an invitation to join.")
          setJoinDialogOpen(false)
          return
        }
      }

      // Get user's internal ID (users.id) from auth_id
      const { data: userProfile } = await supabase
        .from("users")
        .select("id")
        .eq("auth_id", user.id)
        .single()

      if (!userProfile) {
        triggerHaptic('error')
        alert("Could not find your user profile")
        setJoinDialogOpen(false)
        return
      }

      const { error } = await supabase.from("surf_party_participants").insert({
        party_id: selectedPartyId,
        user_id: userProfile.id, // Use users.id (primary key), not auth_id
        travel_method: null,
        receive_notifications: receiveNotifications, // Include notification preference
      })

      if (error) throw error

      triggerHaptic('success')
      setJoinDialogOpen(false)
      setSelectedPartyId(null)
      fetchParties()
    } catch (error) {
      triggerHaptic('error')
      console.error("Error joining party:", error)
      alert("Failed to join party. You may already be a participant.")
      setJoinDialogOpen(false)
    }
  }

  const getTimeDisplay = (party: SurfParty) => {
    if (party.start_time) {
      return party.start_time.slice(0, 5) // HH:MM format
    }
    if (party.time_of_day) {
      return party.time_of_day.charAt(0).toUpperCase() + party.time_of_day.slice(1)
    }
    return "All day"
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-center">
          <Users className="w-8 h-8 animate-pulse text-blue-500 mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">Loading surf parties...</p>
        </div>
      </div>
    )
  }

  return (
    <>
      {/* Join Party Dialog */}
      <Dialog open={joinDialogOpen} onOpenChange={setJoinDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Join Surf Party</DialogTitle>
            <DialogDescription>
              You're about to join this surf party. Would you like to receive notifications for party updates?
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center justify-between py-4">
            <div className="flex items-center gap-2">
              <Label htmlFor="party-notifications" className="text-sm font-medium cursor-pointer">
                Receive party notifications
              </Label>
            </div>
            <Switch
              id="party-notifications"
              checked={receiveNotifications}
              onCheckedChange={setReceiveNotifications}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setJoinDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleJoinParty} className="bg-blue-500 hover:bg-blue-600">
              Join Party
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="relative">
        <div className={`space-y-4 ${(!user || !isPlus) ? "blur-sm" : ""}`}>
        {parties.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 px-6 text-center">
            <div className="w-20 h-20 bg-slate-50 dark:bg-slate-800/50 rounded-full flex items-center justify-center mb-6">
              <Users className="w-10 h-10 text-slate-300 dark:text-slate-600" />
            </div>
            <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2">No upcoming surf parties yet</h3>
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-8 max-w-[240px]">Be the first to organize a party and connect with local riders!</p>
            <Button 
              onClick={onCreateParty} 
              className="h-14 px-8 bg-blue-600 hover:bg-blue-700 text-white rounded-2xl font-bold shadow-xl shadow-blue-600/20 active:scale-95 transition-all flex items-center gap-2"
            >
              <Plus className="w-5 h-5" />
              Organize First Party
            </Button>
          </div>
        ) : (
          <>
            {parties.map((party) => (
              <Card
                key={party.id}
                className="p-4 hover:shadow-md transition-shadow cursor-pointer border-blue-100 dark:border-slate-800 rounded-[2rem] shadow-sm"
                onClick={() => (!user ? undefined : onPartyClick(party.id))}
              >
                <div className="flex items-center justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-lg">{party.activity_type === "kite" ? "🪁" : "🏄"}</span>
                      <h3 className="font-semibold text-sm truncate">{party.title}</h3>
                      {!party.is_public && (
                        <Badge variant="secondary" className="flex items-center gap-1 text-xs">
                          <Lock className="w-3 h-3" />
                          Private
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-3 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {format(new Date(party.party_date), "MMM d")} • {getTimeDisplay(party)}
                      </span>
                      <span>by {party.organizer_name}</span>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 flex-shrink-0">
                    <Badge variant="secondary" className="flex items-center gap-1">
                      <Users className="w-3 h-3" />
                      {party.participant_count}
                    </Badge>
                    {party.is_participant ? (
                      <Badge className="bg-green-500 text-white">Joined</Badge>
                    ) : (
                      <Button
                        size="sm"
                        onClick={(e) => handleJoinClick(party.id, e)}
                        className="bg-blue-500 hover:bg-blue-600"
                        disabled={!user}
                      >
                        Join
                      </Button>
                    )}
                  </div>
                </div>
              </Card>
            ))}

            {user && (
              <Button
                onClick={onCreateParty}
                variant="outline"
                className="w-full border-dashed border-2 border-blue-300 hover:border-blue-400 hover:bg-blue-50 bg-transparent"
              >
                <Plus className="w-4 h-4 mr-2" />
                Organize New Surf Party
              </Button>
            )}
          </>
        )}
      </div>

      {!isPlus && parties.length > 0 && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="pointer-events-auto">
            <Button 
              onClick={onCreateParty}
              className="bg-blue-600 hover:bg-blue-500 text-white rounded-[2rem] h-14 px-8 font-black uppercase tracking-widest shadow-xl shadow-blue-600/20 active:scale-95 transition-all"
            >
              <Lock className="w-4 h-4 mr-2" />
              {user ? "Upgrade to Plus" : "Join Community"}
            </Button>
          </div>
        </div>
      )}
      </div>
    </>
  )
}
